import { Coins, Diamond } from 'lucide-react';
import { useCurrency } from '@/hooks/useCurrency';
import { Skeleton } from '@/components/ui/skeleton';
import { useEffect } from 'react';
import { CurrencyTestButton } from './CurrencyTestButton';

interface CurrencyDisplayProps {
  showFullStats?: boolean;
  className?: string;
}

export function CurrencyDisplay({ showFullStats = false, className = '' }: CurrencyDisplayProps) {
  const { currency, loading, refreshCurrency } = useCurrency();

  // Add manual refresh listener for immediate UI updates
  useEffect(() => {
    const handleRefresh = () => {
      console.log('CurrencyDisplay: Manual refresh triggered');
      refreshCurrency();
    };
    
    window.addEventListener('currency-updated', handleRefresh);
    return () => window.removeEventListener('currency-updated', handleRefresh);
  }, [refreshCurrency]);

  if (loading) {
    return (
      <div className={`flex items-center gap-4 ${className}`}>
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-16" />
      </div>
    );
  }

  if (!currency) {
    return (
      <div className={`flex items-center gap-4 text-muted-foreground ${className}`}>
        <div className="flex items-center gap-1">
          <Coins className="h-4 w-4" />
          <span>--</span>
        </div>
        <div className="flex items-center gap-1">
          <Diamond className="h-4 w-4" />
          <span>--</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
        <Coins className="h-4 w-4" />
        <span className="font-medium">{currency.coins.toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
        <Diamond className="h-4 w-4" />
        <span className="font-medium">{currency.gems.toLocaleString()}</span>
      </div>
      
      {/* Debug Test Buttons - Only visible in development */}
      <CurrencyTestButton />
      
      {showFullStats && (
        <div className="ml-4 text-xs text-muted-foreground">
          <div>Total earned: {currency.lifetime_coins_earned.toLocaleString()} coins</div>
          <div>{currency.lifetime_gems_earned.toLocaleString()} gems</div>
        </div>
      )}
    </div>
  );
}